/**
 * @fileoverview Login form.
 * @description The email/password form itself: client-side validation (via React Hook Form +
 * loginFormSchema), the "remember me" checkbox (which prompts the browser's own password manager
 * to offer saving the credential — see credential-storage.utils.ts; the backend has no concept of
 * this, it never reaches the request body), submit loading/error state, and the "Forgot
 * Password?" link that navigates to the /forgot-password route.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useState, type FormEvent } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useLogin } from '@/features/auth/hooks/useLogin'
import { loginFormSchema, type LoginFormValues } from '@/features/auth/schemas/login-form.schema'
import { storeLoginCredential } from '@/features/auth/utils/credential-storage.utils'
import Button from '@/shared/components/atoms/Button'
import Checkbox from '@/shared/components/atoms/Checkbox'
import Label from '@/shared/components/atoms/Label'
import LinkButton from '@/shared/components/atoms/LinkButton'
import FormField from '@/shared/components/molecules/FormField'
import PasswordField from '@/shared/components/molecules/PasswordField'
import { ROUTES } from '@/shared/constants/routes.constants'

/**
 * @component
 */
function ForgotPasswordLink() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <LinkButton
      data-testid="login-forgot-password-link"
      onClick={() => void navigate(ROUTES.FORGOT_PASSWORD)}
    >
      {t('login.forgotPasswordLink')}
    </LinkButton>
  )
}

/**
 * @component
 */
function LoginForm() {
  const { t } = useTranslation()
  const [rememberMe, setRememberMe] = useState(false)
  const loginMutation = useLogin()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: { identifier: '', password: '' },
  })

  /**
   * @description Submits the login mutation with the schema-validated form values, prompting the
   * browser to save the credential on success when "Remember me" is checked.
   * @param {LoginFormValues} values - The validated identifier/password.
   */
  const onValid = (values: LoginFormValues) => {
    loginMutation.mutate(values, {
      onSuccess: () => {
        if (rememberMe) {
          void storeLoginCredential(values.identifier, values.password)
        }
      },
    })
  }

  /**
   * @description Runs the schema validation and, if valid, calls onValid. Wrapped so the DOM
   * onSubmit handler returns void rather than the Promise React Hook Form's handleSubmit produces.
   * @param {FormEvent<HTMLFormElement>} event - The form submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  return (
    <form
      className="mt-7 flex flex-col gap-[22px]"
      onSubmit={onSubmit}
      noValidate
      data-testid="login-form"
    >
      <FormField
        id="identifier"
        data-testid="login-email-input"
        label={t('login.emailLabel')}
        type="email"
        placeholder={t('login.emailPlaceholder')}
        autoComplete="username"
        error={errors.identifier && t(errors.identifier.message ?? '')}
        {...register('identifier', { onChange: () => loginMutation.reset() })}
      />

      <div>
        <PasswordField
          id="password"
          data-testid="login-password-input"
          label={t('login.passwordLabel')}
          placeholder={t('login.passwordPlaceholder')}
          autoComplete="current-password"
          error={errors.password && t(errors.password.message ?? '')}
          {...register('password', { onChange: () => loginMutation.reset() })}
        />
        <div className="mt-2.5 flex justify-end">
          <ForgotPasswordLink />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Checkbox
          id="rememberMe"
          data-testid="login-remember-me-checkbox"
          checked={rememberMe}
          onChange={(event) => setRememberMe(event.target.checked)}
        />
        <Label htmlFor="rememberMe" variant="inline">
          {t('login.rememberMe')}
        </Label>
      </div>

      {loginMutation.isError && (
        <p role="alert" className="text-[14px] text-red-600" data-testid="login-error-message">
          {loginMutation.error.message}
        </p>
      )}

      <Button type="submit" isLoading={loginMutation.isPending} data-testid="login-submit-button">
        {loginMutation.isPending ? t('login.submitPending') : t('login.submitIdle')}
      </Button>
    </form>
  )
}

export default LoginForm
